// DetailSection — the section-body building block for an entity detail page
// (ADR-036 §Frame constant, body varies). A DetailSection is one bordered, titled
// card (with an optional header action — e.g. an "Edit" affordance). The card's
// body label→value lines are DescriptionList's `Field` (the single consolidated
// label→value primitive; the old DetailRow was folded into Field).

import type { ReactNode } from 'react';

export interface DetailSectionProps {
  /** ReactNode so a caller may prefix a small aria-hidden icon (the Terms page
   *  sections); plain string everywhere else. */
  title: ReactNode;
  /** Optional header-end slot — the section's own act or a view door (e.g. "Edit"), or a quiet fact of the section: a count as the bare number, the title naming it (ADR-036 §7). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Compact size for narrow hosts (the peek): smaller title + padding. */
  dense?: boolean;
}

export function DetailSection({ title, action, children, className = '', dense = false }: DetailSectionProps) {
  // dense IS the peek face (FRONTEND-PATTERNS §Section-body): inside a peek the
  // panel's own <h2> title owns level 2, so a dense section heading is an <h3> —
  // a comfortable (detail-page) section stays <h2> under the page <h1>. Keeps the
  // heading outline honest without a per-consumer level prop.
  const Heading = dense ? 'h3' : 'h2';
  return (
    <section
      className={`rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)] ${dense ? 'p-3.5' : 'p-4'} ${className}`}
    >
      <div className={`flex items-center justify-between gap-2 ${dense ? 'mb-2' : 'mb-3'}`}>
        <Heading className={`font-semibold text-[var(--color-text-primary)] ${dense ? 'text-sm' : 'text-base'}`}>
          {title}
        </Heading>
        {action}
      </div>
      {children}
    </section>
  );
}
